import { Divider, IconButton, Menu, Stack, Tooltip } from "@mui/material";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import { FontSize, TextStyle } from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import "./EmailBodyEditor.css";

const ToolbarButton = ({ active, onClick, disabled, children, title }) => (
    <Tooltip title={title || ""}>
        <span>
            <IconButton
                size="small"
                color={active ? "primary" : "default"}
                onClick={onClick}
                disabled={disabled}
            >
                {children}
            </IconButton>
        </span>
    </Tooltip>
);

const HIGHLIGHT_COLORS = ["#fff59d", "#a5d6a7", "#90caf9", "#f48fb1", "#ffcc80"];
const TEXT_COLORS = ["#000000", "#e53935", "#1e88e5", "#43a047", "#8e24aa", "#fb8c00"];

const ColorPickerButton = ({ icon, title, colors, activeColor, onSelect, onClear }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    return (
        <>
            <ToolbarButton title={title} active={!!activeColor} onClick={(e) => setAnchorEl(e.currentTarget)}>
                {icon}
            </ToolbarButton>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                <Stack direction="row" spacing={1} sx={{ px: 1.5, py: 1 }}>
                    {colors.map((color) => (
                        <IconButton
                            key={color}
                            size="small"
                            onClick={() => {
                                onSelect(color);
                                setAnchorEl(null);
                            }}
                            sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: color,
                                border: activeColor === color ? "2px solid #1976d2" : "1px solid rgba(0,0,0,0.23)",
                                "&:hover": { backgroundColor: color, opacity: 0.8 },
                            }}
                        />
                    ))}
                    <IconButton
                        size="small"
                        onClick={() => {
                            onClear();
                            setAnchorEl(null);
                        }}
                        sx={{
                            width: 24,
                            height: 24,
                            border: "1px solid rgba(0,0,0,0.23)",
                            fontSize: 14,
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </IconButton>
                </Stack>
            </Menu>
        </>
    );
};

export const EmailBodyEditor = forwardRef((props, ref) => {
    const { initialText = "", readOnly = false } = props;

    const editor = useEditor({
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            TextStyle,
            FontSize,
            Color,
            Highlight.configure({ multicolor: true }),
            Image.configure({ allowBase64: true }),
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: initialText,
        editable: !readOnly,
        immediatelyRender: true,
        editorProps: {
            attributes: {
                class: "email-body-editable",
            },
        },
    });

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        editor.commands.setContent(initialText || "");
    }, [editor, initialText]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    useImperativeHandle(ref, () => ({
        setMarkdown: (content) => editor.commands.setContent(content),
        getHtml: () => editor?.getHTML() || "",
    }));

    const handleLinkClick = (event) => {
        if (readOnly && event.target.closest("a")) {
            event.preventDefault();
        }
    };

    if (!editor) return null;

    return (
        <div>
            {!readOnly && (
                <Stack direction="row" spacing={0.5} className="email-body-toolbar">
                    <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                        <UndoIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                        <RedoIcon fontSize="small" />
                    </ToolbarButton>
                    <Divider orientation="vertical" flexItem />
                    <ToolbarButton active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
                        <FormatBoldIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
                        <FormatItalicIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                        <FormatUnderlinedIcon fontSize="small" />
                    </ToolbarButton>
                    <Divider orientation="vertical" flexItem />
                    <ToolbarButton active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                        <FormatListBulletedIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                        <FormatListNumberedIcon fontSize="small" />
                    </ToolbarButton>
                    <Divider orientation="vertical" flexItem />
                    <ToolbarButton active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}>
                        <FormatAlignLeftIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}>
                        <FormatAlignCenterIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}>
                        <FormatAlignRightIcon fontSize="small" />
                    </ToolbarButton>
                    <ToolbarButton active={editor.isActive({ textAlign: "justify" })} onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
                        <FormatAlignJustifyIcon fontSize="small" />
                    </ToolbarButton>
                    <Divider orientation="vertical" flexItem />
                    <ColorPickerButton
                        icon={<FormatColorFillIcon fontSize="small" />}
                        title="Zakreślacz"
                        colors={HIGHLIGHT_COLORS}
                        activeColor={editor.getAttributes("highlight").color}
                        onSelect={(color) => editor.chain().focus().setHighlight({ color }).run()}
                        onClear={() => editor.chain().focus().unsetHighlight().run()}
                    />
                    <ColorPickerButton
                        icon={<FormatColorTextIcon fontSize="small" />}
                        title="Kolor czcionki"
                        colors={TEXT_COLORS}
                        activeColor={editor.getAttributes("textStyle").color}
                        onSelect={(color) => editor.chain().focus().setColor(color).run()}
                        onClear={() => editor.chain().focus().unsetColor().run()}
                    />
                </Stack>
            )}
            <EditorContent editor={editor} onClickCapture={handleLinkClick} />
        </div>
    );
});
